import { Alert as MuiAlert, Link as MuiLink, AlertColor, Box } from '@mui/material';
import { Button } from '@components/index';
import { styled } from '@mui/material/styles';
import { Link } from 'react-router-dom';
import { CheckCircleSolid, ExclamationTriangleSolid, ExclamationCircleSolid, InfoCircle } from '@assets/svgs';
import { AlertStatus } from '@models/alert';
import { TIcon, TAlertProps } from '@components/alert/Alert.types';
import { Tooltip } from '@components/tooltip/Tooltip';
import { getTypographyVariants } from '@theme/styleOverrides';

const isExternalUrl = (url: string) => /^(https?:)?\/\//i.test(url);

const severity = {
  [AlertStatus.Info]: 'info' as AlertColor,
  [AlertStatus.Warning]: 'warning' as AlertColor,
  [AlertStatus.Incident]: 'error' as AlertColor,
  [AlertStatus.Promotion]: 'success' as AlertColor,
  [AlertStatus.SiteUpdateAlert]: 'siteUpdateAlert' as AlertColor,
};

const iconTypes: TIcon = {
  [AlertStatus.Warning]: <ExclamationTriangleSolid />,
  [AlertStatus.Incident]: <ExclamationCircleSolid />,
  [AlertStatus.Promotion]: <CheckCircleSolid />,
  [AlertStatus.Info]: <InfoCircle />,
  [AlertStatus.SiteUpdateAlert]: <ExclamationCircleSolid />,
};

const StyledAlert = styled(MuiAlert)<TAlertProps>(({ theme }) => ({
  minHeight: '36px',
  top: 0,
  left: 0,
  right: 0,
  borderRadius: 0,
  ...getTypographyVariants(theme.palette.fontSize).caption,
  fontWeight: theme.typography.fontWeightBold,
  zIndex: 2,
  alignItems: 'center',
  display: 'grid',
  gridTemplateColumns: '40px 14fr 4fr',
  color: theme.palette.common.black,
  '&.MuiPaper-root.MuiAlert-root': {
    width: 'unset',
  },
  '& a': {
    color: theme.palette.common.black,
  },
  '& .MuiButton-root': {
    color: theme.palette.common.black,
    '&:disabled': {
      opacity: '0.6 !important',
    },
  },
  '&.warning': {
    backgroundColor: theme.palette.warning.main,
  },
  '&.success': {
    backgroundColor: theme.palette.success.main,
    svg: {
      fill: theme.palette.success.main,
    },
  },
  '&.info': {
    backgroundColor: theme.palette.info.main,

    '& button': {
      color: theme.palette.common.black,
    },
  },
  '&.error': {
    backgroundColor: theme.palette.error.main,
    color: theme.palette.common.white,
    '& button': {
      color: theme.palette.common.white,
    },
    '& a': {
      color: theme.palette.common.white,
    },
  },
  '&.siteUpdateAlert': {
    backgroundColor: theme.palette.button.tertiaryDark,
    color: theme.palette.common.white,
    '& button': {
      color: theme.palette.common.white,
    },
  },

  '.MuiAlert-icon': {
    margin: '0',
    padding: '0',
    justifyContent: 'center',
    '& svg': theme.icons.xs,
  },
  '.MuiAlert-message': {
    padding: 0,
    paddingTop: '2px',
    margin: '0',
  },
  '.MuiAlert-action': {
    marginRight: 0,
    padding: '0 16px',
    '& a': {
      textDecoration: 'none',
    },
    '& .MuiButton-root': {
      marginRight: '16px',
      fontSize: getTypographyVariants(theme.palette.fontSize).caption.fontSize,
      lineHeight: getTypographyVariants(theme.palette.fontSize).caption.lineHeight,
      padding: 0,
      '& span': {
        '&:hover': {
          textDecoration: 'underline',
        },
      },
    },
  },
}));

export const Alert = ({
  type,
  ctaText,
  ctaUrl,
  ctaFunction,
  dismiss,
  ctaDisabled,
  ctaHoverText,
  variant = 'filled',
  ctaSxProps,
  ...others
}: TAlertProps) => {
  const icon = type !== undefined ? iconTypes[type] : null;

  return (
    <StyledAlert
      {...others}
      variant={variant}
      type={type}
      icon={icon}
      className={severity[type]}
      severity={severity[type]}
      action={
        <Tooltip title={ctaHoverText ?? null} placement='bottom-end'>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            {ctaText ? (
              <span style={{ display: 'flex' }}>
                {ctaUrl ? (
                  isExternalUrl(ctaUrl) ? (
                    <a href={ctaUrl} target='_blank' rel='noopener noreferrer'>
                      <Button id='btn' disabled={ctaDisabled} variant='text' sx={ctaSxProps}>
                        {ctaText}
                      </Button>
                    </a>
                  ) : (
                    <MuiLink to={ctaUrl} component={Link}>
                      <Button id='btn' disabled={ctaDisabled} variant='text' sx={ctaSxProps}>
                        {ctaText}
                      </Button>
                    </MuiLink>
                  )
                ) : (
                  <Button disabled={ctaDisabled} variant='text' onClick={ctaFunction}>
                    {ctaText}
                  </Button>
                )}
              </span>
            ) : null}
            {dismiss ? (
              <span style={{ display: 'flex' }}>
                {type !== 'Incident' && (
                  <Button disabled={ctaDisabled} variant='text' onClick={dismiss}>
                    Dismiss
                  </Button>
                )}
              </span>
            ) : null}
          </Box>
        </Tooltip>
      }
    />
  );
};
